import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../components/Shell";
import { api, subjectMeta } from "../lib/api";
import { Users, FileText, ListChecks, EnvelopeSimple, GraduationCap, ArrowLeft, DownloadSimple, Image as ImageIcon, Trash, Clock, CurrencyCircleDollar } from "@phosphor-icons/react";
import { toast } from "sonner";

const dateLabel = (iso) => {
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
};

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [imgStats, setImgStats] = useState(null);
  const [llmCosts, setLlmCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/users").then((r) => setUsers(r.data)),
      api.get("/admin/image-stats").then((r) => setImgStats(r.data)).catch(() => {}),
      api.get("/admin/llm-costs").then((r) => setLlmCosts(r.data)).catch(() => {}),
    ]).catch(() => toast.error("Admin verisi yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  const open = async (u) => {
    setSelected(u);
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/users/${u.id}`);
      setDetail(data);
    } catch { toast.error("Detay alınamadı"); }
    finally { setDetailLoading(false); }
  };

  const downloadCsv = () => {
    if (!users.length) { toast.warning("İndirilecek kayıt yok."); return; }
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n;]/.test(s) ? `"${s}"` : s;
    };
    const header = [
      "email","child_name","grade","parent_emails","is_admin",
      "submissions_count","tests_count","avg_test_score","created_at","last_activity"
    ];
    const rows = users.map((u) => [
      u.email,
      u.child_name,
      u.grade,
      (u.parent_emails || []).join("|"),
      u.is_admin ? "yes" : "no",
      u.submissions_count,
      u.tests_count,
      u.avg_test_score ?? "",
      u.created_at,
      u.last_activity,
    ].map(escape).join(","));
    const csv = "\ufeff" + [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `odevix-kullanicilar-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${users.length} kayıt indirildi`);
  };

  if (selected) {
    return (
      <Shell title="Admin · Kullanıcı detayı">
        <button
          onClick={() => { setSelected(null); setDetail(null); }}
          data-testid="admin-back-button"
          className="mb-4 flex items-center gap-2 text-brand-ink2 font-nunito font-semibold text-sm"
        >
          <ArrowLeft size={18} /> Listeye dön
        </button>

        <section className="bg-white rounded-3xl p-5 shadow-sm mb-4" data-testid="admin-user-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-brand-primary/20 flex items-center justify-center">
              <GraduationCap size={24} weight="duotone" color="#FFA200" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-outfit font-extrabold text-xl text-brand-ink truncate">{selected.child_name || "—"}</div>
              <div className="font-nunito text-xs text-brand-ink2 truncate">{selected.email}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm font-nunito">
            <div className="bg-brand-paperAlt rounded-2xl p-3">
              <div className="text-brand-ink2 text-xs">Sınıf</div>
              <div className="font-bold text-brand-ink">{selected.grade || "—"}</div>
            </div>
            <div className="bg-brand-paperAlt rounded-2xl p-3">
              <div className="text-brand-ink2 text-xs">Kayıt</div>
              <div className="font-bold text-brand-ink">{dateLabel(selected.created_at)}</div>
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xs font-nunito font-semibold text-brand-ink2 mb-1">Veli e-postaları</div>
            <div className="flex flex-wrap gap-2" data-testid="admin-parent-emails">
              {(selected.parent_emails || []).length === 0 && <span className="text-sm font-nunito text-brand-ink2">—</span>}
              {(selected.parent_emails || []).map((e) => (
                <span key={e} className="px-3 py-1 rounded-full bg-brand-paperAlt font-nunito text-xs flex items-center gap-1">
                  <EnvelopeSimple size={14} /> {e}
                </span>
              ))}
            </div>
          </div>
        </section>

        {detailLoading && <div className="text-center font-nunito text-brand-ink2 py-8">Yükleniyor...</div>}

        {detail && (
          <>
            <section className="mb-5">
              <div className="text-xs uppercase tracking-widest font-nunito font-extrabold text-brand-ink mb-2">
                Ödevler ({detail.submissions.length})
              </div>
              <div className="flex flex-col gap-2" data-testid="admin-submissions-list">
                {detail.submissions.length === 0 && <div className="text-sm font-nunito text-brand-ink2">Henüz ödev yok.</div>}
                {detail.submissions.map((s) => {
                  const sm = subjectMeta(s.subject);
                  return (
                    <div key={s.id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3" data-testid={`admin-sub-${s.id}`}>
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: sm.bg }}>
                        <FileText size={20} weight="duotone" color={sm.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-outfit font-bold text-sm text-brand-ink">{sm.name}</div>
                        <div className="text-xs font-nunito text-brand-ink2">{dateLabel(s.created_at)}</div>
                      </div>
                      <div className="flex gap-2 text-xs font-nunito font-bold">
                        <span className="text-good">D {s.correct}</span>
                        <span className="text-bad">Y {s.incorrect}</span>
                        <span className="text-blank">B {s.blank}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="mb-5">
              <div className="text-xs uppercase tracking-widest font-nunito font-extrabold text-brand-ink mb-2">
                Testler ({detail.tests.length})
              </div>
              <div className="flex flex-col gap-2" data-testid="admin-tests-list">
                {detail.tests.length === 0 && <div className="text-sm font-nunito text-brand-ink2">Henüz test yok.</div>}
                {detail.tests.map((t) => (
                  <div key={t.id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3" data-testid={`admin-test-${t.id}`}>
                    <div className="w-10 h-10 rounded-2xl bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                      <ListChecks size={20} weight="duotone" color="#FFA200" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-outfit font-bold text-sm text-brand-ink">
                        {t.subject ? subjectMeta(t.subject).name : "Genel"} {t.test_no ? `· Test_${t.test_no}` : ""}
                      </div>
                      <div className="text-xs font-nunito text-brand-ink2">{dateLabel(t.created_at)}</div>
                    </div>
                    <div className="font-outfit font-extrabold text-lg text-brand-ink">{t.score != null ? `${t.score}/10` : "—"}</div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </Shell>
    );
  }

  return (
    <Shell title="Admin · Kullanıcılar">
      <div className="flex items-center gap-2 mb-4">
        <Users size={26} weight="duotone" color="#FFA200" />
        <h1 className="font-outfit font-extrabold text-2xl text-brand-ink">Kullanıcılar</h1>
        <span className="ml-auto text-sm font-nunito text-brand-ink2">{users.length} kayıt</span>
      </div>

      <button
        onClick={downloadCsv}
        disabled={!users.length}
        data-testid="admin-download-csv"
        className="mb-4 w-full bg-brand-ink text-white font-outfit font-bold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform"
      >
        <DownloadSimple size={20} weight="bold" color="#FFB703" />
        CSV indir ({users.length} kayıt)
      </button>

      {llmCosts && (
        <div className="mb-4 bg-white rounded-3xl p-4 shadow-sm border-l-4 border-brand-primary" data-testid="admin-llm-costs">
          <div className="flex items-center gap-2 mb-3">
            <CurrencyCircleDollar size={20} weight="duotone" color="#FFA200" />
            <div className="font-outfit font-bold text-brand-ink text-sm">LLM maliyeti (tahmini)</div>
            <span className="ml-auto text-[10px] uppercase tracking-widest font-nunito font-bold text-brand-ink2">USD/TRY: {llmCosts.usd_try_rate}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-brand-paperAlt rounded-2xl p-3" data-testid="admin-llm-day">
              <div className="text-[11px] font-nunito text-brand-ink2 uppercase tracking-wider">Son 24 saat</div>
              <div className="font-outfit font-extrabold text-brand-ink text-xl">₺{llmCosts.day.cost_try.toFixed(2)}</div>
              <div className="text-[11px] font-nunito text-brand-ink2 mt-1">{llmCosts.day.events} çağrı · {llmCosts.day.unique_students} öğrenci</div>
            </div>
            <div className="bg-brand-primary/15 rounded-2xl p-3" data-testid="admin-llm-month">
              <div className="text-[11px] font-nunito text-brand-ink2 uppercase tracking-wider">Son 30 gün</div>
              <div className="font-outfit font-extrabold text-brand-ink text-xl">₺{llmCosts.month.cost_try.toFixed(2)}</div>
              <div className="text-[11px] font-nunito text-brand-ink2 mt-1">{llmCosts.month.events} çağrı · {llmCosts.month.unique_students} öğrenci</div>
            </div>
          </div>
          <div className="bg-good-bg rounded-2xl p-3 mb-3" data-testid="admin-llm-avg">
            <div className="text-[11px] font-nunito text-brand-ink2 uppercase tracking-wider">Çocuk başına aylık ortalama</div>
            <div className="font-outfit font-extrabold text-good text-2xl">₺{llmCosts.month.avg_cost_try_per_student.toFixed(2)}</div>
            <div className="text-[11px] font-nunito text-brand-ink2 mt-1">
              in: {llmCosts.month.input_tokens.toLocaleString("tr-TR")} tok · out: {llmCosts.month.output_tokens.toLocaleString("tr-TR")} tok
            </div>
          </div>
          {llmCosts.month_by_model?.length > 0 && (
            <details className="text-xs font-nunito" data-testid="admin-llm-breakdown">
              <summary className="cursor-pointer text-brand-ink2">Model bazında dökümü ({llmCosts.month_by_model.length})</summary>
              <ul className="mt-2 space-y-1">
                {llmCosts.month_by_model.map((row, i) => (
                  <li key={i} className="flex items-center gap-2 text-brand-ink2 py-1 border-b border-black/5 last:border-0">
                    <span className="font-bold text-brand-ink">{row.model}</span>
                    <span>· {row.kind === "analysis" ? "analiz" : "pratik test"}</span>
                    <span className="ml-auto font-bold text-brand-ink">₺{row.cost_try.toFixed(2)}</span>
                    <span>({row.events})</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="mt-2 text-[11px] font-nunito text-brand-ink2 italic">{llmCosts.note}</div>
        </div>
      )}

      {imgStats && (
        <div className="mb-5 bg-white rounded-3xl p-4 shadow-sm border-l-4 border-brand-ink" data-testid="admin-image-stats">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={20} weight="duotone" color="#1D3557" />
            <div className="font-outfit font-bold text-brand-ink text-sm">Fotoğraf saklama</div>
            <span className="ml-auto text-[10px] uppercase tracking-widest font-nunito font-bold text-brand-ink2">48s tampon</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-brand-paperAlt rounded-2xl p-3">
              <div className="text-xs font-nunito text-brand-ink2">Şu an saklanan</div>
              <div className="font-outfit font-extrabold text-brand-ink text-xl" data-testid="admin-images-stored">{imgStats.images_currently_stored}</div>
            </div>
            <div className="bg-bad-bg rounded-2xl p-3">
              <div className="text-xs font-nunito text-brand-ink2">Son 24s silinen</div>
              <div className="font-outfit font-extrabold text-bad text-xl flex items-center gap-1" data-testid="admin-images-deleted">
                <Trash size={16} weight="duotone" /> {imgStats.deleted_last_24h}
              </div>
            </div>
          </div>
          {imgStats.recent_cleanups?.length > 0 && (
            <details className="mt-3 text-xs font-nunito">
              <summary className="cursor-pointer text-brand-ink2">Son temizlik log ({imgStats.recent_cleanups.length})</summary>
              <ul className="mt-2 space-y-1">
                {imgStats.recent_cleanups.map((e, i) => (
                  <li key={i} className="flex items-center gap-2 text-brand-ink2">
                    <Clock size={12} /> {new Date(e.at).toLocaleString("tr-TR")} · <b className="text-brand-ink">{e.deleted_count}</b> silindi
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {loading && <div className="text-center font-nunito text-brand-ink2 py-8">Yükleniyor...</div>}

      <div className="flex flex-col gap-3" data-testid="admin-users-list">
        {users.map((u) => (
          <button
            key={u.id}
            data-testid={`admin-user-${u.id}`}
            onClick={() => open(u)}
            className="w-full text-left bg-white rounded-2xl p-4 shadow-sm border border-transparent hover:border-brand-primary/40 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-brand-primary/20 flex items-center justify-center flex-shrink-0">
                <GraduationCap size={22} weight="duotone" color="#FFA200" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-outfit font-bold text-brand-ink truncate">
                  {u.child_name} <span className="text-xs font-nunito text-brand-ink2">· {u.grade}</span>
                </div>
                <div className="text-xs font-nunito text-brand-ink2 truncate">{u.email}</div>
              </div>
              <div className="text-right">
                <div className="font-outfit font-extrabold text-brand-ink text-base leading-none">{u.submissions_count}+{u.tests_count}</div>
                <div className="text-[10px] font-nunito text-brand-ink2 mt-1">öd · test</div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-nunito text-brand-ink2">
              <span>Ort. test: <b className="text-brand-ink">{u.avg_test_score != null ? `${u.avg_test_score}/10` : "—"}</b></span>
              <span>·</span>
              <span>Veliler: <b className="text-brand-ink">{(u.parent_emails || []).length}</b></span>
              <span>·</span>
              <span>Son: <b className="text-brand-ink">{dateLabel(u.last_activity)}</b></span>
            </div>
          </button>
        ))}
        {!loading && users.length === 0 && <div className="text-center text-brand-ink2 font-nunito py-10">Kullanıcı yok.</div>}
      </div>
    </Shell>
  );
}
